import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { appSettingsKeys, auditKeys } from "@/lib/query";

export interface RecipientTeams {
  postDelivery: string;
  deliveryCompliance: string;
}

export function useRecipientTeams() {
  return useQuery({
    queryKey: appSettingsKeys.recipientTeams,
    queryFn: async (): Promise<RecipientTeams> => {
      const res = await fetch("/api/settings/recipient-teams");
      if (!res.ok) throw new Error("Failed to load recipient teams");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useSaveRecipientTeams() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: RecipientTeams) => {
      const res = await fetch("/api/settings/recipient-teams", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to save recipient teams");
      const json = await res.json();
      return json.data as RecipientTeams;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(appSettingsKeys.recipientTeams, data);
      toast.success("Recipient teams saved");
    },
    onError: () => {
      toast.error("Failed to save recipient teams");
    },
  });
}

export interface GuidanceRequestTemplate {
  template: string;
}

export function useGuidanceRequestTemplate() {
  return useQuery({
    queryKey: appSettingsKeys.guidanceRequestTemplate,
    queryFn: async (): Promise<GuidanceRequestTemplate> => {
      const res = await fetch("/api/settings/guidance-request-template");
      if (!res.ok) throw new Error("Failed to load guidance request template");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useSaveGuidanceRequestTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: GuidanceRequestTemplate) => {
      const res = await fetch("/api/settings/guidance-request-template", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to save guidance request template");
      const json = await res.json();
      return json.data as GuidanceRequestTemplate;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(appSettingsKeys.guidanceRequestTemplate, data);
      toast.success("Guidance request template saved");
    },
    onError: () => {
      toast.error("Failed to save guidance request template");
    },
  });
}

export interface KickoffEmailTemplate {
  subject: string;
  body: string;
  cc: string[];
}

export function useKickoffEmailTemplate() {
  return useQuery({
    queryKey: appSettingsKeys.kickoffEmailTemplate,
    queryFn: async (): Promise<KickoffEmailTemplate> => {
      const res = await fetch("/api/settings/kickoff-email-template");
      if (!res.ok) throw new Error("Failed to load kickoff email template");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useSaveKickoffEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: KickoffEmailTemplate) => {
      const res = await fetch("/api/settings/kickoff-email-template", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to save kickoff email template");
      return json.data as KickoffEmailTemplate;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(appSettingsKeys.kickoffEmailTemplate, data);
      toast.success("Kickoff email template saved");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to save kickoff email template");
    },
  });
}

export function useStaleAuditDays() {
  return useQuery({
    queryKey: appSettingsKeys.staleAudit,
    queryFn: async (): Promise<number> => {
      const res = await fetch("/api/settings/stale-audit");
      if (!res.ok) throw new Error("Failed to load the attention threshold");
      const json = await res.json();
      return json.data.days;
    },
  });
}

export function useSaveStaleAuditDays() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (days: number) => {
      const res = await fetch("/api/settings/stale-audit", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ days }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to save the attention threshold");
      return json.data.days as number;
    },
    onSuccess: (days) => {
      queryClient.setQueryData(appSettingsKeys.staleAudit, days);
      // Which audits are flagged depends on the threshold.
      queryClient.invalidateQueries({ queryKey: auditKeys.all });
      toast.success("Attention threshold saved");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to save the attention threshold");
    },
  });
}
